import { useState, useEffect } from "react";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Inbox, Check, X, Trash2, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";

const STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

export default function AdminVenueClaims() {
  const { t } = useTranslation();
  const [claims, setClaims] = useState([]);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeClaim, setActiveClaim] = useState(null);

  const fetchAll = async (status = statusFilter) => {
    setLoading(true);
    try {
      const params = status === "all" ? "" : `?status=${status}`;
      const [claimsRes, usersRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/venue-claims${params}`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/venue-plans`),
      ]);
      setClaims(claimsRes.data || []);
      setUsers(usersRes.data?.users || []);
      setPlans(plansRes.data || []);
    } catch {
      toast.error(t("admin_venue_claims.load_failed", { defaultValue: "Failed to load claims" }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(statusFilter); }, [statusFilter]); // eslint-disable-line

  const filtered = claims.filter((c) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(q)
      || (c.email || "").toLowerCase().includes(q)
      || (c.venue_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-venue-claims-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2">
          <Inbox className="h-6 w-6 text-primary" />
          {t("admin_venue_claims.title", { defaultValue: "Venue Claims" })} ({filtered.length})
        </h1>
      </div>

      <div className="bg-card rounded-xl border border-border mb-6 p-4 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("admin_venue_claims.search_placeholder", { defaultValue: "Search by name, email or venue..." })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="venue-claims-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="venue-claims-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">{STATUS_LABELS.pending}</SelectItem>
            <SelectItem value="approved">{STATUS_LABELS.approved}</SelectItem>
            <SelectItem value="rejected">{STATUS_LABELS.rejected}</SelectItem>
            <SelectItem value="all">{t("admin_venue_claims.status_all", { defaultValue: "All" })}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-16 rounded-lg" />)}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("admin_venue_claims.col_venue", { defaultValue: "Venue" })}</TableHead>
                <TableHead>{t("admin_venue_claims.col_claimer", { defaultValue: "Claimer" })}</TableHead>
                <TableHead>{t("admin_venue_claims.col_role", { defaultValue: "Role" })}</TableHead>
                <TableHead>{t("admin_venue_claims.col_status", { defaultValue: "Status" })}</TableHead>
                <TableHead>{t("admin_venue_claims.col_submitted", { defaultValue: "Submitted" })}</TableHead>
                <TableHead className="text-right">{t("admin_venue_claims.col_actions", { defaultValue: "Actions" })}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                  {t("admin_venue_claims.no_claims", { defaultValue: "No claims match this filter." })}
                </TableCell></TableRow>
              ) : filtered.map((claim) => (
                <TableRow key={claim.claim_id} data-testid={`claim-row-${claim.claim_id}`}>
                  <TableCell className="font-medium">{claim.venue_name}</TableCell>
                  <TableCell>
                    <div>{claim.name}</div>
                    <div className="text-xs text-muted-foreground">{claim.email}</div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{claim.business_role || "—"}</TableCell>
                  <TableCell>
                    <StatusBadge status={claim.status} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {claim.created_at ? new Date(claim.created_at).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => setActiveClaim(claim)}
                      data-testid={`claim-review-${claim.claim_id}`}>
                      {t("admin_venue_claims.review", { defaultValue: "Review" })}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ReviewClaimDialog
        claim={activeClaim}
        users={users}
        plans={plans}
        onClose={() => setActiveClaim(null)}
        onResolved={() => {
          setActiveClaim(null);
          fetchAll(statusFilter);
        }}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const colour =
    status === "approved" ? "bg-green-100 text-green-700"
    : status === "rejected" ? "bg-rose-100 text-rose-700"
    : "bg-amber-100 text-amber-700";
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colour}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function ReviewClaimDialog({ claim, users, plans, onClose, onResolved }) {
  const { t } = useTranslation();
  const [assignUser, setAssignUser] = useState("__none__");
  const [assignPlan, setAssignPlan] = useState("verified_profile");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset every time a different claim is opened so previous selections
  // don't leak between reviews.
  useEffect(() => {
    if (claim) {
      setAssignUser(claim.assigned_user_id || "__none__");
      setAssignPlan(claim.assigned_plan_key || "verified_profile");
      setNotes(claim.admin_notes || "");
    }
  }, [claim]);

  if (!claim) return null;

  const submit = async (status) => {
    setBusy(true);
    try {
      const body = { status, admin_notes: notes };
      if (status === "approved") {
        if (assignUser && assignUser !== "__none__") body.assign_user_id = assignUser;
        if (assignPlan) body.assign_plan_key = assignPlan;
      }
      await axios.put(`${API}/admin/venue-claims/${claim.claim_id}`, body);
      toast.success(t("admin_venue_claims.toast_resolved", {
        defaultValue: "Claim {{status}}", status,
      }));
      onResolved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("admin_venue_claims.confirm_delete", { defaultValue: "Delete this claim?" }))) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/venue-claims/${claim.claim_id}`);
      toast.success(t("admin_venue_claims.toast_deleted", { defaultValue: "Claim deleted" }));
      onResolved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!claim} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="claim-review-dialog">
        <DialogHeader>
          <DialogTitle>
            {t("admin_venue_claims.review_title", { defaultValue: "Review claim — {{venue}}", venue: claim.venue_name })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted/40 rounded-lg p-3 text-sm space-y-1">
            <p><strong>{claim.name}</strong> — {claim.business_role || "—"}</p>
            <p className="text-muted-foreground">{claim.email}{claim.phone ? ` · ${claim.phone}` : ""}</p>
            {claim.message && <p className="pt-2 italic">"{claim.message}"</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("admin_venue_claims.assign_user", { defaultValue: "Assign user (owner)" })}</Label>
              <Select value={assignUser} onValueChange={setAssignUser}>
                <SelectTrigger className="mt-1" data-testid="claim-assign-user">
                  <SelectValue placeholder={t("admin_venue_claims.select_user", { defaultValue: "Select user" })} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("admin_venue_claims.no_user", { defaultValue: "— Don't assign —" })}</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.email} ({u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("admin_venue_claims.assign_plan", { defaultValue: "Assign plan" })}</Label>
              <Select value={assignPlan} onValueChange={setAssignPlan}>
                <SelectTrigger className="mt-1" data-testid="claim-assign-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.plan_key} value={p.plan_key}>
                      {p.name} (€{p.price_monthly_eur}/mo)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>{t("admin_venue_claims.notes", { defaultValue: "Internal notes" })}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1"
              data-testid="claim-notes" />
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button onClick={() => submit("approved")} disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="claim-approve-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
              {t("admin_venue_claims.approve", { defaultValue: "Approve & assign" })}
            </Button>
            <Button onClick={() => submit("rejected")} variant="outline" disabled={busy}
              className="text-rose-600 hover:text-rose-700" data-testid="claim-reject-btn">
              <X className="h-4 w-4 mr-1" />
              {t("admin_venue_claims.reject", { defaultValue: "Reject" })}
            </Button>
            <Button onClick={handleDelete} variant="ghost" size="icon" disabled={busy}
              className="ml-auto text-muted-foreground hover:text-destructive" data-testid="claim-delete-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
